import type { ProductUnknown } from "@/common/contracts/product.contracts";
import { getPriceInfo, isProductCurated } from "@/common/utils/product.utils";
import type { ProductCardProduct } from "@/features/search/components/product-card/product-card.context";

/**
 * Turns a product from the gateway into one the product card can render.
 *
 * The card was written for the search service, which works out prices and badges before it sends
 * them. The gateway doesn't: it sends a nested `auction`, a separate `original_price`, and the raw
 * flags the labels used to be worked out from. This function is the only place that translation
 * happens, so the homepage blocks, the PDP rows, account favourites, the blog and the 404 page all
 * show the same card as search instead of a second copy of it.
 *
 * The discount percentage comes from `getPriceInfo`, the same helper the old card used, so a badge
 * shows the same number it showed before. The prices themselves are read off the product directly,
 * because `getPriceInfo` turns a missing buy-now price into 0 and the card would print "€ 0" where
 * it can show the asking price instead.
 *
 * A flag the query didn't ask for comes out false. That matches what the old card did: the seller's
 * other-ads grid runs on an older fragment with no `isCurated` in it, and has never shown a curated
 * label.
 */
/**
 * The search service's `listingPrices`, from the gateway's `Price.unconverted` on each card price:
 * the euros a converted card came from (WHO-5991 decision 7). Null when the prices weren't
 * converted (no display currency, EUR, a missing rate, or the viewer's own listing).
 */
const toListingPrices = (
  product: ProductUnknown
): ProductCardProduct["listingPrices"] => {
  const buyNow = product.auction?.buy_now_price?.unconverted;
  const original = product.original_price?.unconverted;
  const soldAt = product.auction?.sold_at?.unconverted;
  const minimumBid = product.auction?.minimum_bid?.unconverted;
  const currency =
    buyNow?.currency ??
    original?.currency ??
    soldAt?.currency ??
    minimumBid?.currency;

  if (!currency) {
    return null;
  }

  return {
    currency,
    originalPrice: original?.amount ?? null,
    buyNowPrice: buyNow?.amount ?? null,
    bidFrom: minimumBid?.amount ?? null,
    soldAtPrice: soldAt?.amount ?? null,
  };
};

export const toCardProduct = (product: ProductUnknown): ProductCardProduct => {
  const { price, soldAtPrice, bidPrice, discounted, discountPercentage } =
    getPriceInfo(product);

  const sold = !!product.auction?.sold_at;
  const allowBid = product.auction?.allow_bid ?? false;

  return {
    id: product.id ?? "",
    slug: product.slug ?? "",
    title: product.title ?? "",
    currency: price.currency,
    originalPrice: product.original_price?.amount ?? null,
    buyNowPrice: product.auction?.buy_now_price?.amount ?? null,
    /* Null rather than 0 — the card shows the badge whenever this isn't null, so a 0 would put a
     * "-0%" badge on every full-price item. */
    discountPercentage: discounted ? discountPercentage : null,
    /* You can't bid on something that's already sold. */
    bidFrom: allowBid && !sold ? bidPrice?.amount ?? null : null,
    sold,
    soldAtPrice: soldAtPrice?.amount ?? null,
    listingPrices: toListingPrices(product),
    numberOfItems: product.number_of_items ?? null,
    /* An image can come back without a url. The card shows the first two in the list, so a gap
     * would be an empty frame — drop those instead of passing them on. */
    images: (product.images ?? []).flatMap(image =>
      image?.url
        ? [
            {
              url: image.url,
              width: image.width ?? null,
              height: image.height ?? null,
            },
          ]
        : []
    ),
    favoriteId: product.favorite?.id ?? null,
    labels: {
      curated: isProductCurated(product),
      boosted: product.isBoosted ?? false,
      showroom: product.showroomModel ?? false,
    },
  };
};
